import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import { cardHeaderClass, cardHeaderTitleClass, cardShellClass } from '@app/constants/styles';
import { formatEur } from './budget';
import { type OutOfPocketLine } from './insights';
import { gridHeadTint } from './sprintGrid';

const card = `flex h-full flex-col ${cardShellClass}`;
const figure = 'w-32 text-right tabular-nums';
// Shorter than the Spend grid's rows: this is a summary, and the card sits beside a chart.
const cell = 'h-8 border-b border-r border-border px-3';
const headCell = `${cell} ${gridHeadTint} text-sm font-medium text-foreground`;

/** The whole year, scrolling: "the rest are small" is the reader's judgement, not the card's. */
const TopOutOfPocketCard = ({
  lines,
  year,
  action,
}: {
  lines: OutOfPocketLine[];
  year: string;
  /** A control in the header's corner, e.g. the arrow into Spend. */
  action?: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const total = lines.reduce((sum, line) => sum + line.total, 0);

  return (
    <section className={card}>
      <div className={cardHeaderClass}>
        <div className={cardHeaderTitleClass}>
          <h3 className="text-base font-semibold text-foreground">{t('insights.cards.outOfPocket.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('insights.cards.outOfPocket.hint', { year })}</p>
        </div>
        {action}
      </div>

      {!lines.length ? (
        <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">{t('insights.cards.outOfPocket.none')}</p>
      ) : (
        <div className="flex flex-1 flex-col px-5 max-md:px-4 pb-5">
          {/* The total sticks to the foot while the bills scroll behind it. */}
          <div className="max-h-72 overflow-auto rounded-xl border border-border">
            <table className="w-full table-fixed border-separate border-spacing-0 [&_tr>*:last-child]:border-r-0 [&>tbody>tr:last-child>*]:border-b-0 [&>tfoot>tr>*]:border-b-0">
              <thead className="sticky top-0 z-10 bg-card">
                <tr>
                  <th scope="col" className={cx(headCell, 'text-left')}>
                    {t('insights.cards.outOfPocket.cost')}
                  </th>
                  <th scope="col" className={cx(headCell, figure)}>
                    {t('insights.cards.outOfPocket.totalColumn')}
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {lines.map(line => (
                  <tr key={`${line.section}:${line.name}`}>
                    <th
                      scope="row"
                      className={cx(cell, 'truncate text-left font-normal text-foreground')}
                      title={line.name}
                    >
                      {line.name}
                    </th>
                    <td className={cx(cell, figure, 'text-foreground')}>{formatEur(line.total)}</td>
                  </tr>
                ))}
              </tbody>
              {/* The line over the total is the foot's own, so a scrolled row cannot hide it. */}
              <tfoot className="sticky bottom-0 bg-card text-sm [&>tr>*]:border-t">
                <tr>
                  <th scope="row" className={cx(cell, gridHeadTint, 'text-left font-medium text-foreground')}>
                    {t('insights.cards.outOfPocket.total')}
                  </th>
                  <td className={cx(cell, gridHeadTint, figure, 'font-semibold text-foreground')}>
                    {formatEur(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

export default TopOutOfPocketCard;
